import { useState, useEffect, useRef } from "react";
import { ChevronRight } from "lucide-react";

const STORAGE_KEY = "hasSeenMatchUp";

// Text & headline are baked into each PNG — no UI <h2>/<p> needed.
const SLIDES = [
  "/onboarding/1-ballon.png",
  "/onboarding/2-sifflet.png",
  "/onboarding/3-drapeau.png",
  "/onboarding/4-couronne.png",
  "/onboarding/5-trio.png",
];

/**
 * Full-screen 5-slide onboarding shown once per browser (gated by localStorage
 * `hasSeenMatchUp`). The illustrations already contain their own title +
 * description as baked typography, so this component renders ZERO native text
 * besides the brand mark and the CTA. Sits ABOVE the LoginPage — dismissing
 * via "C'EST PARTI !" or "Passer" reveals the email/password form.
 *
 * All 5 PNGs are eagerly preloaded on mount so swipes between slides feel
 * instant (no white flash between transitions).
 */
export default function OnboardingCarousel() {
  const [visible, setVisible] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) !== "true"; }
    catch { return true; }
  });
  const [index, setIndex] = useState(0);
  // Track touch start X for swipe detection. Threshold: 50px.
  const touchStartXRef = useRef(null);

  // Eagerly warm all 5 slides on mount for instant transitions.
  useEffect(() => {
    if (!visible) return;
    SLIDES.forEach((src) => {
      const i = new Image();
      i.src = src;
    });
  }, [visible]);

  // Lock background scroll while the overlay is up.
  useEffect(() => {
    if (!visible) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [visible]);

  if (!visible) return null;

  const isLast = index === SLIDES.length - 1;

  const handleNext = () => {
    if (isLast) {
      try { localStorage.setItem(STORAGE_KEY, "true"); } catch {}
      setVisible(false);
    } else {
      setIndex((i) => i + 1);
    }
  };

  const handleSkip = () => {
    try { localStorage.setItem(STORAGE_KEY, "true"); } catch {}
    setVisible(false);
  };

  // ---------- Touch swipe (>50px) ----------
  const handleTouchStart = (e) => {
    touchStartXRef.current = e.touches[0]?.clientX ?? null;
  };
  const handleTouchEnd = (e) => {
    const start = touchStartXRef.current;
    if (start == null) return;
    const end = e.changedTouches[0]?.clientX ?? start;
    const dx = end - start;
    touchStartXRef.current = null;
    if (dx <= -50 && !isLast) setIndex((i) => i + 1);
    else if (dx >= 50 && index > 0) setIndex((i) => i - 1);
  };

  return (
    <div
      data-testid="onboarding-carousel"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      style={{
        position: "fixed", inset: 0, zIndex: 12000,
        background: "#000000",
        display: "flex", flexDirection: "column",
        paddingTop: "calc(env(safe-area-inset-top, 0px) + 12px)",
        paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 24px)",
      }}
    >
      {/* Top bar: skip + brand */}
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        padding: "8px 20px 12px",
      }}>
        <span className="font-heading" style={{
          fontSize: 16, fontWeight: 800, letterSpacing: "0.04em",
          color: "#22c55e",
        }}>
          MATCHUP
        </span>
        {!isLast && (
          <button
            type="button"
            data-testid="onboarding-skip"
            onClick={handleSkip}
            style={{
              background: "transparent", border: "none",
              color: "#6b7280", fontSize: 13, fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Passer
          </button>
        )}
      </div>

      {/* Image — text & headline are baked-in. Force contain so nothing crops. */}
      <div
        key={`img-${index}`}
        style={{
          flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
          padding: "8px 16px",
          animation: "matchup-fade-in 280ms ease-out",
          minHeight: 0, // critical so flex child can shrink
        }}
      >
        <img
          src={SLIDES[index]}
          alt=""
          loading="eager"
          decoding="async"
          fetchPriority="high"
          data-testid={`onboarding-image-${index}`}
          style={{
            width: "100%",
            height: "auto",
            maxWidth: "100%",
            maxHeight: "100%",
            objectFit: "contain",
          }}
        />
      </div>

      {/* Dots */}
      <div style={{
        display: "flex", justifyContent: "center", alignItems: "center",
        gap: 6, padding: "18px 0 14px",
      }}>
        {SLIDES.map((_, i) => (
          <span
            key={i}
            data-testid={`onboarding-dot-${i}`}
            style={{
              display: "inline-block",
              width: i === index ? 22 : 7,
              height: 7,
              borderRadius: 999,
              background: i === index ? "#22c55e" : "#27272a",
              transition: "all 240ms ease",
            }}
          />
        ))}
      </div>

      {/* CTA */}
      <div style={{ padding: "0 20px" }}>
        <button
          type="button"
          data-testid={isLast ? "onboarding-finish" : "onboarding-next"}
          onClick={handleNext}
          style={{
            width: "100%", height: 54, borderRadius: 16,
            background: "linear-gradient(135deg,#22c55e 0%,#16a34a 100%)",
            color: "#fff", fontWeight: 800, fontSize: 15,
            letterSpacing: isLast ? "0.08em" : "0",
            border: "none", cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            gap: 8, boxShadow: "0 8px 24px rgba(34,197,94,0.35)",
          }}
          className="active:scale-[0.98] transition-transform"
        >
          {isLast ? "C'EST PARTI !" : (
            <>
              Suivant <ChevronRight size={18} />
            </>
          )}
        </button>
      </div>

      <style>{`
        @keyframes matchup-fade-in {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
      `}</style>
    </div>
  );
}
